import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function SpareShopsPage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: getString(query.text),
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const normalizedText = (searchParams.text ?? '').toLowerCase();
    const isNearMeSearch = normalizedText.includes('near me') || normalizedText.includes('near-me');
    const locationSuffix = hasLocationFilter
        ? `in ${locationLabel}`
        : isNearMeSearch
          ? 'near me'
          : 'in South Africa';

    const titleLocationLabel = hasLocationFilter
        ? `in ${locationLabel}`
        : isNearMeSearch
          ? 'Near Me'
          : 'in South Africa';

    const pageTitle = hasLocationFilter
        ? `Spare Shops in ${locationLabel} | Car parts spares services`
        : isNearMeSearch
          ? 'Spare Shops Near Me | Car parts spares services'
          : 'Spare Shops in South Africa | Car parts spares services';

    const pageDescription = hasLocationFilter
        ? `Find trusted spare shops in ${locationLabel}. Browse profiles of local car parts suppliers, auto parts stores, and spares specialists near you. Search by suburb or category now.`
        : 'Find trusted spare shops in South Africa. Browse profiles of local car parts suppliers, auto parts stores, and spares specialists near you. Search by suburb or category now.';

    const baseUrl = `${ENV.DOMAIN_URL}/spare-shops-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.text) canonicalParams.set('text', searchParams.text);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Soweto', 'Midrand'],
        Pretoria: ['Centurion', 'Hatfield', 'Menlyn', 'Brooklyn', 'Silverton'],
        'Cape Town': ['Bellville', 'Century City', 'Milnerton', 'Woodstock', 'Somerset West'],
        Durban: ['Umhlanga', 'Pinetown', 'Westville', 'Berea', 'Amanzimtoti'],
        Gqeberha: ['Walmer', 'Newton Park', 'Summerstrand', 'Korsten', 'Uitenhage'],
        Bloemfontein: ['Universitas', 'Langenhoven Park', 'Brandwag', 'Westdene', 'Hilton'],
    };

    const suburbLinks = cityName && suburbLinksByCity[cityName] ? suburbLinksByCity[cityName] : [];
    const locationKeywords = hasLocationFilter ? locationLabel : 'South Africa';
    const keywordList = [
        `spare shops ${locationKeywords}`,
        `car parts spares services ${locationKeywords}`,
        `spare shops in ${locationKeywords}`,
        `auto parts suppliers ${locationKeywords}`,
        `local spare shops ${locationKeywords}`,
    ].join(', ');

    const faqItems = [
        {
            question: `How do I find spare shops ${locationSuffix}?`,
            answer: hasLocationFilter
                ? `You are filtering for ${locationLabel}. Use the province, city, or suburb filters to widen or narrow results, or clear filters to see all spare shops.`
                : 'Use the province, city, or suburb filters to focus results. You can also type spare shops near me to prioritize nearby listings.',
        },
        {
            question: `Which car parts services are listed ${locationSuffix}?`,
            answer: 'The directory includes new and used car parts, OEM parts, aftermarket parts, engine parts, body panels, electrical components, suspension parts, and accessories. Filter by category to match the parts you need.',
        },
        {
            question: 'How accurate are the province, city, and suburb filters?',
            answer: 'Locations come from supplier profiles. Start with province, then refine to city or suburb to get the most relevant spare shops.',
        },
        {
            question: `Can I compare spare shops ${locationSuffix}?`,
            answer: 'Yes. Shortlist suppliers, review services and locations, and contact multiple spare shops before deciding.',
        },
        {
            question: 'What should I include when requesting quotes?',
            answer: 'Share part numbers, vehicle make and model, year, and preferred location. This helps spare shops respond with accurate pricing and availability.',
        },
    ];

    const directoryConfig = {
        industryName: 'Car Spares',
        businessDirectoryPath: 'spare-shops-near-me',
        businessDetailPath: 'business',
        categoryId: ENV.CATEGORY_ID,
        siteName: 'CarSparesEzyFind',
        websiteHost: 'www.CarSparesEzyFind.co.za',
        defaultCategorySlug: 'spare-shops-near-me',
        extraKeywords: [
            'Spare shops near me',
            'spare shops city',
            'spare shops johannesburg',
            'spare shops cape town',
            'spare shops durban',
            'spare shops pretoria',
        ],
    };

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywordList} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'LocalBusiness',
                                    '@id': canonicalUrl,
                                    name: `Spare Shops ${titleLocationLabel}`,
                                    description: pageDescription,
                                    url: canonicalUrl,
                                    areaServed: locationKeywords,
                                    knowsAbout: [
                                        'Car Spare Parts',
                                        'Auto Parts',
                                        'Engine Parts',
                                        'Body Panels',
                                        'Electrical Components',
                                        'Suspension Parts',
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: {
                                            '@type': 'Answer',
                                            text: item.answer,
                                        },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>
            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(220, 38, 38, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(239, 68, 68, 0.3), transparent 40%), radial-gradient(circle at 65% 85%, rgba(248, 113, 113, 0.25), transparent 45%)",
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.3em] text-red-200">Car Spares Directory</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                Spare Shops {titleLocationLabel}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-red-100">
                                Discover spare shops, car parts suppliers, and automotive services with precise filters and location
                                shortcuts.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Start filtering
                                </Link>
                                <Link
                                    href="/spare-shops-near-me?text=spare+shops+near+me"
                                    className="bg-red-500 text-white px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Spare shops near me
                                </Link>
                                <span className="text-xs text-red-100">New: quick city shortcuts below.</span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-red-100">Quick start searches</p>
                                    <span className="rounded-full bg-red-500/20 px-2.5 py-1 text-xs text-red-100">
                                        Popular
                                    </span>
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <Link
                                        href="/spare-shops-near-me/gauteng/city-of-johannesburg-metropolitan-municipality/johannesburg"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Spare Shops Johannesburg
                                    </Link>
                                    <Link
                                        href="/spare-shops-near-me/western-cape/cape-town/"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Spare Shops Cape Town
                                    </Link>
                                    <Link
                                        href="/spare-shops-near-me/kwazulu-natal/durban"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Spare Shops Durban
                                    </Link>
                                    <Link
                                        href="/spare-shops-near-me/gauteng/pretoria"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-red-300"
                                    >
                                        Spare Shops Pretoria
                                    </Link>
                                </div>
                                <div className="mt-5 grid gap-3 sm:grid-cols-3 text-xs text-red-100">
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Filter by category</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Shortlist suppliers</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Compare locations</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-2xl font-semibold text-slate-900">Discover Car Parts & Spare Shops Near You</h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Looking for spare shops {locationSuffix}? CarSparesEzyFind helps you connect
                                with verified spare shops across South Africa, from engine parts and body panels to
                                electrical components and accessories. Use the filters to narrow by province, city, or suburb,
                                then compare parts availability and supplier profiles in one streamlined directory.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                If you are searching for spare shops near me in Sandton, auto parts in
                                Roodepoort, or car spares in Randburg, this page is designed to surface the most
                                relevant options. Start with a broad location, then refine to specific suburbs to see the
                                nearest suppliers. For national procurement, clear the filters to browse spare shops
                                in South Africa and compare specialists across provinces.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Popular searches include spare shops Johannesburg Gauteng, car parts services Johannesburg,
                                spare shops in City of Johannesburg, and local auto parts specialists Gauteng.
                                Whether you need engine parts, gearbox parts, brake systems, or body panels, you can
                                shortlist suppliers and request quotes with confidence.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                To strengthen local outcomes, look for businesses with consistent NAP details and active
                                Google Business profiles. This helps validate credentials and ensures fast response times for
                                urgent parts enquiries.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-slate-900">Top Car Parts Categories</h3>
                            <ul className="mt-4 space-y-2 text-sm text-slate-600">
                                <li>Engine parts and components</li>
                                <li>Body panels and exterior parts</li>
                                <li>Electrical components and wiring</li>
                                <li>Suspension and steering parts</li>
                                <li>Brake systems and clutch parts</li>
                            </ul>
                            <h3 className="mt-6 text-lg font-semibold text-slate-900">Why Choose Local Spare Shops</h3>
                            <p className="mt-3 text-sm text-slate-600">
                                Local teams reduce delivery times, simplify collection, and improve part verification. Working with
                                nearby suppliers helps you get back on the road faster.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">Browse by city</h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Fast links for spare shops city searches and metro areas.
                                    </p>
                                </div>
                                <Link
                                    href="/spare-shops-near-me?text=spare+shops+city"
                                    className="text-sm font-semibold text-red-700"
                                >
                                    View all cities
                                </Link>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickCities.map((city) => (
                                    <Link
                                        key={city.name}
                                        href={`/spare-shops-near-me/${city.province}/${slugify(city.name)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                    >
                                        {city.name}
                                    </Link>
                                ))}
                            </div>
                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 1</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Pick a category</p>
                                    <p className="mt-1 text-xs text-slate-600">Choose car parts types and categories.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 2</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Set location</p>
                                    <p className="mt-1 text-xs text-slate-600">Province → city → suburb for accuracy.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 3</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Compare listings</p>
                                    <p className="mt-1 text-xs text-slate-600">Review locations, services, and suppliers.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Browse by province</h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Filter by province, then drill down to city and suburb.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickProvinces.map((province) => (
                                    <Link
                                        key={province}
                                        href={`/spare-shops-near-me/${slugify(province)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                    >
                                        {province}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 rounded-2xl border border-red-100 bg-red-50 p-5">
                            <h3 className="text-sm font-semibold text-slate-900">Quick tips</h3>
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                <li>Start with a province to speed up results.</li>
                                <li>Use city filters for spare shops Johannesburg or spare shops Durban searches.</li>
                                <li>Search company names to jump straight to a supplier.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10" aria-labelledby="suburb-links-heading">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 id="suburb-links-heading" className="text-xl font-semibold text-slate-900">
                        {cityName
                            ? `Spare Shops by Suburb in ${cityName}`
                            : 'Spare Shops by City in South Africa'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        {cityName
                            ? `Explore spare shops in ${cityName} suburbs to find local specialists near your location.`
                            : 'Jump to popular cities and metros to find spare shops near you.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {cityName && suburbLinks.length > 0
                            ? suburbLinks.map((suburb) => (
                                  <Link
                                      key={suburb}
                                      href={`/spare-shops-near-me/${cityToProvinceMap[cityName] ?? slugify(cityName)}/${slugify(cityName)}/${slugify(suburb)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                  >
                                      {`Spare Shops in ${suburb}`}
                                  </Link>
                              ))
                            : quickCities.map((city) => (
                                  <Link
                                      key={city.name}
                                      href={`/spare-shops-near-me/${city.province}/${slugify(city.name)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-red-400 hover:text-red-700"
                                  >
                                      {`Spare Shops in ${city.name}`}
                                  </Link>
                              ))}
                    </div>
                </div>
            </section>

            <div className="my-10">
                <div className="container" id="business-filters">
                    <BusinessList
                        searchParams={searchParams}
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-red-700">Car Spares Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                Spare shops {locationSuffix}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Use this page to find spare shops near me searches, or drill into a spare shops province,
                                city, or suburb. Results update instantly as you apply filters.
                            </p>
                            <p className="mt-4 text-sm text-slate-600">
                                Browse local spare shops, auto parts suppliers, car parts dealers, and accessories stores in
                                one place. Save time by comparing services, locations, and categories side-by-side.
                            </p>
                            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                                {hasLocationFilter
                                    ? `Showing location filters for ${locationLabel}. Change province, city, or suburb to explore nearby areas.`
                                    : 'Tip: Start with a province, then narrow by city or suburb for tighter local results.'}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">
                                Frequently Asked Questions (Local Spare Shops SEO)
                            </h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <DownloadApp />
        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: String(ENV.CATEGORY_ID),
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';
    
    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
